/**
 * State Machine React Hook
 * 
 * Provides current state and nextEvents for UI rendering.
 * Client-side state is strictly advisory - PostgreSQL remains authoritative.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../services/api';

export interface StateMachineState {
  processKey: string;
  state: string;
  version: number;
  businessKey: string;
  nextEvents: string[];
  loading: boolean;
  error?: string;
}

export interface TransitionOptions {
  payload?: unknown;
  expectedVersion?: number;
}

export interface UseStateMachineResult {
  state: StateMachineState;
  transition: (event: string, options?: TransitionOptions) => Promise<void>;
  refresh: () => Promise<void>;
  history: Array<{
    event: string;
    fromState: string;
    toState: string;
    at: Date;
    actorId?: string;
  }>;
  loadHistory: () => Promise<void>;
}

/**
 * Hook for managing state machine entities
 */
export function useStateMachine(
  entityType: 'quote',
  entityId: string
): UseStateMachineResult {
  const [state, setState] = useState<StateMachineState>({
    processKey: entityType,
    state: 'unknown',
    version: 0,
    businessKey: '',
    nextEvents: [],
    loading: true
  });

  const [history, setHistory] = useState<Array<{
    event: string;
    fromState: string;
    toState: string;
    at: Date;
    actorId?: string;
  }>>([]);

  /**
   * Load current state from server
   */
  const loadState = useCallback(async () => {
    // Don't load if entityId is empty or placeholder
    if (!entityId || entityId === 'placeholder') {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    // Handle demo quotes with mock data
    if (entityId.startsWith('demo-quote-')) {
      setState({
        processKey: 'quote',
        state: 'new',
        version: 1,
        businessKey: entityId.toUpperCase(),
        nextEvents: ['APPROVE'],
        loading: false
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: undefined }));

      const response = await apiClient.getQuoteState(entityId);
      
      setState({
        processKey: response.processKey,
        state: response.state,
        version: response.version,
        businessKey: response.businessKey,
        nextEvents: response.nextEvents || [],
        loading: false
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load state'
      }));
    }
  }, [entityId]);

  /**
   * Load state history from server
   */
  const loadHistory = useCallback(async () => {
    // Don't load if entityId is empty or placeholder
    if (!entityId || entityId === 'placeholder') {
      return;
    }

    // Handle demo quotes with mock history
    if (entityId.startsWith('demo-quote-')) {
      setHistory([
        {
          event: 'CREATE',
          fromState: 'initial',
          toState: 'new',
          at: new Date(),
          actorId: 'demo-user'
        }
      ]);
      return;
    }

    try {
      const response = await apiClient.getQuoteHistory(entityId);
      setHistory(response.history || []);
    } catch (error) {
      console.error('Failed to load state history:', error);
    }
  }, [entityId]);

  /**
   * Execute a state transition
   */
  const transition = useCallback(async (event: string, options?: TransitionOptions) => {
    // Don't execute if entityId is empty or placeholder
    if (!entityId || entityId === 'placeholder') {
      throw new Error('No valid quote selected for transition');
    }

    // Handle demo quotes with mock transitions
    if (entityId.startsWith('demo-quote-')) {
      setState(prev => ({ ...prev, loading: true, error: undefined }));
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock state transitions
      let newState = state.state;
      let nextEvents: string[] = [];
      
      switch (event) {
        case 'APPROVE':
          newState = 'approved';
          nextEvents = ['PREPARE_FOR_PAYMENT_COLLECTION', 'REVIEW'];
          break;
        // REJECT removed in v2 - quotes stay in 'new' state if not approved
        case 'REVIEW':
          newState = 'new';
          nextEvents = ['APPROVE'];
          break;
        case 'PREPARE_FOR_PAYMENT_COLLECTION':
          newState = 'ready_for_payment_collection';
          nextEvents = ['INITIATE_PAYMENT', 'COLLECTION_EXPIRED'];
          break;
        case 'INITIATE_PAYMENT':
          newState = 'payment_initiated';
          nextEvents = ['RECEIVE_FULL_PAYMENT_1', 'RECEIVE_PARTIAL_PAYMENT_1', 'PAYMENT_FAILED'];
          break;
        case 'RECEIVE_FULL_PAYMENT_1':
          newState = 'activated';
          nextEvents = [];
          break;
        case 'RECEIVE_PARTIAL_PAYMENT_1':
          newState = 'partial_payment_received';
          nextEvents = ['RECEIVE_FULL_PAYMENT_2', 'COLLECTION_EXPIRED'];
          break;
        case 'RECEIVE_FULL_PAYMENT_2':
          newState = 'activated';
          nextEvents = [];
          break;
        case 'PAYMENT_FAILED':
          newState = 'ready_for_payment_collection';
          nextEvents = ['INITIATE_PAYMENT', 'COLLECTION_EXPIRED'];
          break;
        case 'COLLECTION_EXPIRED':
          newState = 'payment_collection_expired';
          nextEvents = [];
          break;
        case 'UPDATE':
          // Stay in same state for updates
          nextEvents = state.nextEvents;
          break;
        default:
          throw new Error(`Invalid transition: ${event}`);
      }
      
      setState(prev => ({
        ...prev,
        state: newState,
        version: prev.version + 1,
        nextEvents,
        loading: false
      }));

      // Add to mock history
      setHistory(prev => [...prev, {
        event,
        fromState: state.state,
        toState: newState,
        at: new Date(),
        actorId: 'demo-user'
      }]);
      
      console.log(`🎯 Demo transition: ${state.state} → ${newState} (${event})`);
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: undefined }));

      const response = await apiClient.transitionQuote(entityId, {
        event,
        payload: options?.payload,
        expectedVersion: options?.expectedVersion || state.version,
        requestId: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      });

      setState({
        processKey: response.processKey,
        state: response.state,
        version: response.version,
        businessKey: response.businessKey,
        nextEvents: response.nextEvents || [],
        loading: false
      });

      // Reload history after transition
      await loadHistory();
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Transition failed'
      }));
      throw error;
    }
  }, [entityId, state.version, state.state, state.nextEvents, loadHistory]);

  /**
   * Initial load
   */
  useEffect(() => {
    loadState();
    loadHistory();
  }, [loadState, loadHistory]);

  return {
    state,
    transition,
    refresh: loadState,
    history,
    loadHistory
  };
}
